import { getColorHexValue } from 'theme-utilities';
import { Color, CylinderGeometry, Mesh, MeshBasicMaterial } from 'three';
import ThreeGlobe from 'three-globe';
import { camera, controls, scene } from '../../location-world.js';
import countries from './../../json/globe-data-min.json';
import { gConf } from './config.js';
import { SELECTORS } from './init';
import * as Utils from './utils';

// Get globe data from the data attribute -> If necessary then we can replace it with an API call
const gData = JSON.parse(document.querySelector(SELECTORS.ctr).dataset.locations);

const getMarker = () => {
	return `
  <div class="marker__icon">
    <svg viewBox="0 0 36 36">
      <circle fill="${getColorHexValue('body')}" stroke="currentColor" cx="18" cy="18" stroke-width="3" r="16"></circle>
      <circle fill="currentColor" cx="18" cy="18" r="6"></circle>
    </svg>
  </div>`;
};

export let Globe;

// Store all country cIds without duplicates / That means we have one or more offices
export const countriesWithHQ = { cIds: [...new Set(gData.map((d) => d.cid))], mids: {}, coords: {} };

const addMarkersToGlobe = () => {
	// Adds little "dots" to the globe with represent the countries
	Globe.hexPolygonsData(countries.features)
		.hexPolygonResolution(3)
		.hexPolygonMargin(0.35)
		// If we have a office in that given country, change the default color to our primary color
		.hexPolygonColor((e) => {
			return countriesWithHQ.cIds.includes(e.properties.ISO_A2) ? getColorHexValue('primary') : getColorHexValue('primary', 0.2);
		})
		.showAtmosphere(true)
		.atmosphereColor(getColorHexValue('primary'))
		.atmosphereAltitude(0.25)
		// Places the office markers
		.htmlAltitude(0)
		.htmlElementsData(gData)
		.htmlElement(() => {
			const markerCtr = document.createElement('div');
			markerCtr.innerHTML = getMarker();
			return markerCtr;
		});
};

const addLongLatLinesToGlobe = () => {
	// Lines of longitude
	const lineThickness = 0.1;
	const lineAltitude = 0.2;
	const LatLongLineColor = new Color(getColorHexValue('primary', 0.2));

	let longMesh, latMesh;

	for (let n = 0; n < 24; ++n) {
		const line = new CylinderGeometry(gConf.globe.radius + lineAltitude, gConf.globe.radius + lineAltitude, lineThickness, 50, 1, true);
		line.translate(0, -lineThickness / 2, 0);
		line.rotateX(Math.PI / 2);
		line.rotateY((n * Math.PI) / 12);
		longMesh = new Mesh(line, new MeshBasicMaterial({ color: LatLongLineColor }));
		Globe.add(longMesh);
	}

	// Lines of latitude
	for (let n = 1; n < 12; ++n) {
		const lat = ((n - 6) * Math.PI) / 12;
		const r = gConf.globe.radius * Math.cos(lat);
		const y = gConf.globe.radius * Math.sin(lat);
		const r1 = r - (lineThickness * Math.sin(lat)) / 2;
		const r2 = r + (lineThickness * Math.sin(lat)) / 2;
		const line = new CylinderGeometry(r1 + lineAltitude, r2 + lineAltitude, Math.cos(lat) * lineThickness, 50, 8, true);
		line.translate(0, (-Math.cos(lat) * lineThickness) / 2 + y, 0);
		latMesh = new Mesh(line, new MeshBasicMaterial({ color: LatLongLineColor }));
		Globe.add(latMesh);
	}
};

const fillCountriesCenterPoints = () => {
	const countriesCenterPoints = Array();

	countriesWithHQ.cIds.forEach((c) => {
		// Set geojson features (country coordinates and so on) of countries where we have an office
		// Get the center lat & lng coordinates of the center of the countries bounding box
		const midObj = Utils.getCenterCoordinates(countries.features.filter((f) => f.properties.ISO_A2 === c)[0]);
		countriesWithHQ.mids[c] = midObj;
		countriesCenterPoints.push({ lat: midObj.geometry.coordinates[1], lng: midObj.geometry.coordinates[0] });
	});
};

export const initGlobe = () => {
	// Fill Center Points
	fillCountriesCenterPoints();

	// init Globe
	Globe = new ThreeGlobe();

	// Set the globe material
	const globeMaterial = Globe.globeMaterial();
	globeMaterial.color = new Color(getColorHexValue('body'));
	globeMaterial.emissive = new Color(getColorHexValue('body'));
	globeMaterial.emissiveIntensity = 1.0;

	// First array entry in cIds defines the initial starting point.
	Globe.onGlobeReady(() => Utils.goToCoords(countriesWithHQ.cIds[0]));

	// Set the Point of view of the camera
	Globe.setPointOfView(camera.position, Globe.position);

	// Add the long and lat lines to the globe
	addLongLatLinesToGlobe();

	// Set the globe texture
	addMarkersToGlobe();
	// testCountriesCenterPoints();

	controls.addEventListener('change', () => {
		Globe.setPointOfView(camera.position, Globe.position);
	});

	scene.add(Globe);
};
